"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { getAuth, isSignInWithEmailLink } from "firebase/auth";

import { useAuth } from "@/lib/features/auth/useAuth";
import { ROUTES } from "@/lib/routes";
import { Toaster as ReactHotToaster } from "react-hot-toast";
import {
  getEmailForSigninLocalStorage,
  getAuthTokenLocalStorage,
  getUserDataLocalStorage,
  getUserLocalStorage,
  resetEverything,
} from "@/lib/local_storage";
import LoadingScreen from "@/components/login/LoadingScreen";

const Page = () => {
  const router = useRouter();
  const auth = getAuth();

  const {
    googleSignIn,
    isAuthenticated,
    magicLinkSignIn,
    customTokenSignin,
    userData,
  } = useAuth();
  // null = not checked yet, avoids flashing the Google login button
  // before we know whether we're in the Chrome extension iframe.
  const [isInsideAnIframe, setIsInsideAnIframe] = useState<boolean | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsInsideAnIframe(window.self !== window.top);
  }, []);

  useEffect(() => {
    if (!isInsideAnIframe) return;

    let cancelled = false;

    // Listen for auth token from the Chrome extension (stored in chrome.storage.local)
    const handleExtAuth = async (event: Event) => {
      const { customToken } = (event as CustomEvent).detail || {};
      if (cancelled || !customToken) return;
      cancelled = true;

      try {
        setIsLoading(true);
        await customTokenSignin(customToken, true);
        // customTokenSignin handles navigation on success
      } catch {
        // Token expired or invalid – fall through to email login
        console.warn(
          "[sign-in] Extension auto-auth failed, falling back to email login",
        );
        router.replace(ROUTES.TOKEN_SIGN_IN);
      } finally {
        setIsLoading(false);
      }
    };

    document.addEventListener("sendblue-extension-auth", handleExtAuth);
    // Ask the extension's content script for a stored auth token
    document.dispatchEvent(new CustomEvent("sendblue-extension-ready"));

    // If no response within 1.5s, fall back to normal email login
    const timeout = setTimeout(() => {
      if (!cancelled) {
        cancelled = true;
        router.replace(ROUTES.TOKEN_SIGN_IN);
      }
    }, 1500);

    return () => {
      cancelled = true;
      document.removeEventListener("sendblue-extension-auth", handleExtAuth);
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInsideAnIframe]);

  useEffect(() => {
    const zapRedirect = new URLSearchParams(window.location.search).get(
      "redirect_uri",
    );
    if (isAuthenticated && !zapRedirect) {
      // Don't consume the redirect URL here — handleLogin in useAuthSession
      // is the authoritative consumer. Reading it here would race and clear it
      // before handleLogin gets to it.
      router.push(ROUTES.CONVERSATIONS);
    }
  }, [isAuthenticated, router, ROUTES.CONVERSATIONS]);

  const handleSignInWithGoogle = async () => {
    try {
      setIsLoading(true);
      resetEverything();
      await googleSignIn(true);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = getEmailForSigninLocalStorage();

      if (!email) {
        email = window.prompt("Please provide your email for confirmation");
      }

      if (!email) return;

      setIsLoading(false);
      magicLinkSignIn(email.trim().toLowerCase(), window.location.href);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasLoginData = () => {
      const authToken = getAuthTokenLocalStorage();
      const userData = getUserDataLocalStorage();
      const user = getUserLocalStorage();

      const hasValidAuthToken = authToken && authToken.trim() !== "";

      const hasValidUserData =
        userData && userData.trim() !== "" && userData !== "{}";

      const hasValidUser = user && Object.keys(user).length > 0;

      return !!(hasValidAuthToken || hasValidUserData || hasValidUser);
    };

    if (hasLoginData()) {
      const timeout = setTimeout(() => {
        setIsLoading(false);
      }, 5000);

      return () => clearTimeout(timeout);
    } else {
      setIsLoading(false);
    }
  }, []);

  // Show loader while:
  // - isLoading (auth data check in progress)
  // - iframe check hasn't completed yet (null)
  // - inside an iframe (Chrome extension — will redirect to TOKEN_SIGN_IN or auto-auth)
  if (isLoading || isInsideAnIframe === null || isInsideAnIframe) {
    return (
      <>
        <LoadingScreen />
        <ReactHotToaster />
      </>
    );
  }

  const handleEmailLogin = async () => {
    resetEverything();
    router.push("/sign-in/token");
  };

  return (
    <>
      <div className="mb-6 flex flex-col items-center font-medium sm:mb-8">
        <h1 className="mb-2 text-[32px] leading-[38.4px] text-ttwilight-navy sm:text-[61px] sm:leading-[91.5px]">
          <span className="welcomeTitle italic text-primary">Welcome</span> back
        </h1>
        <span className="font-AeonikProLight text-center text-base font-normal text-grey-subtext">
          Sign in with Google or enter your email
        </span>
      </div>
      <div className="w-full">
        <button
          className="flex w-full items-center justify-center gap-4 rounded-lg border border-grey-neutral bg-white py-2 font-medium leading-[19.2px]"
          disabled={isLoading}
          onClick={handleSignInWithGoogle}
          type="button"
        >
          <Image
            src={"/assets/icons/google.svg"}
            alt="Google icon"
            width={24}
            height={24}
          ></Image>
          Continue with Google
        </button>
        <div className="flex items-center">
          <div className="w-full border-t border-grey-neutral"></div>
          <p className="mx-2 my-4 text-grey-subtext">OR</p>
          <div className="w-full border-t border-grey-neutral"></div>
        </div>
        <div className="flex justify-center">
          <button
            className="h-10 w-full rounded-lg bg-ttwilight-navy text-base font-medium leading-[19.2px] text-white"
            disabled={isLoading}
            onClick={() => handleEmailLogin()}
            type="button"
          >
            Email Login
          </button>
        </div>
      </div>
      <ReactHotToaster />
    </>
  );
};
export default Page;
